import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeskLayout } from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  RefreshCw, Compass, ArrowRight, CheckCircle2, Loader2, KeyRound, Copy,
  LayoutDashboard, ListTodo, Inbox, Clock, Star, Users, Building2, FileText,
  Lock, AlertCircle, ShieldAlert, Wand2, ExternalLink,
} from 'lucide-react';
import { resetDemo, getDemoStatus, seedShowcaseData, purgeShowcaseFromLive, getLiveTodayDiagnostic, getLiveOtherTabsDiagnostic, sandboxRecordIds, mintDemoResetLink } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

// Curated walkthrough — one signature scenario per module
const WALKTHROUGH = [
  {
    n: 1,
    icon: KeyRound,
    title: 'Log in',
    path: '/auth',
    blurb: 'Sign in as the prospect-facing showcase account.',
    state: 'Sign in with the showcase account (admin issues a reset link).',
  },
  {
    n: 2,
    icon: LayoutDashboard,
    title: 'Today',
    path: '/desk/today',
    blurb: 'Open dashboard with a single urgent guest message and one queued request.',
    state: 'Handle Now: 1 · Due Today: 2 · Requests queue: 1',
  },
  {
    n: 3,
    icon: ListTodo,
    title: 'Daily Run',
    path: '/desk/daily-run',
    blurb: 'Five lanes filled cleanly — Handle Now, Due Today (x2), Waiting On, Scheduled. No carry-over yet.',
    state: 'Sarah: Access blocked · Late checkout · Airport pickup · Wi-Fi vendor · Mid-stay clean',
  },
  {
    n: 4,
    icon: Inbox,
    title: 'Guest Inbox',
    path: '/desk/inbox',
    blurb: 'Open Sarah\u2019s thread: 4 messages, 2 from guest + 2 from operator. SLA timer live.',
    state: 'Sarah Johnson · WhatsApp · "Locked out — can\u2019t access the property"',
  },
  {
    n: 5,
    icon: Clock,
    title: 'Follow-ups',
    path: '/desk/followups',
    blurb: 'One post check-in welcome follow-up due later today.',
    state: 'Sarah Johnson · POST_CHECKIN_WELCOME · due in ~3 hours',
  },
  {
    n: 6,
    icon: Star,
    title: 'Reviews',
    path: '/desk/reviews',
    blurb: 'A 4-star Airbnb review waiting on a draft reply.',
    state: 'Airbnb · 4★ · "WiFi dropped twice…"',
  },
  {
    n: 7,
    icon: Users,
    title: 'Guests',
    path: '/desk/guests',
    blurb: 'One guest profile — VIP tag, 3-night stay, notes for context.',
    state: 'Sarah Johnson · checking in today',
  },
  {
    n: 8,
    icon: Building2,
    title: 'Client Workspaces',
    path: '/desk/properties',
    blurb: 'One showcase workspace with FAQs including the late check-in policy.',
    state: 'The Hill Villa · WiFi: MarisDemo2026',
  },
  {
    n: 9,
    icon: FileText,
    title: 'Reports → Concierge Briefing',
    path: '/desk/reports',
    blurb: 'Live briefing reflects all of the above. Switch period to "Today" — yellow banner shows the report is still LIVE.',
    state: 'PDF + Send Briefing locked until End Shift',
  },
  {
    n: 10,
    icon: Lock,
    title: 'Close the shift',
    path: '/desk/daily-run',
    blurb: 'Run End Shift. The Concierge Briefing snapshots into a closed, exportable report. Banner turns green.',
    state: 'Past briefings (eye icon) now shows 1 closed report ready for audit/share',
  },
];

const Step = ({ step, onOpen }) => {
  const Icon = step.icon;
  return (
    <Card className="border-stone-200 hover:border-[#3F617E]/40 transition-colors">
      <CardContent className="p-5 flex gap-4 items-start">
        <div className="flex-shrink-0">
          <div className="h-10 w-10 rounded-xl bg-[#3F617E]/10 text-[#3F617E] grid place-items-center font-semibold">
            {step.n}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon className="h-4 w-4 text-stone-500" />
            <h3 className="text-sm font-semibold text-stone-900">{step.title}</h3>
          </div>
          <p className="text-sm text-stone-600 leading-snug">{step.blurb}</p>
          <p className="text-xs text-stone-400 mt-1.5 italic">{step.state}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full flex-shrink-0"
          onClick={() => onOpen(step.path)}
          data-testid={`demo-step-${step.n}-open`}
        >
          Open <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
};

const Demo = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [resetting, setResetting] = useState(false);
  const [seedingShowcase, setSeedingShowcase] = useState(false);
  const [showcaseSummary, setShowcaseSummary] = useState(null);
  const [status, setStatus] = useState({ is_demo: false });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const [lastPurge, setLastPurge] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [sandboxing, setSandboxing] = useState(false);
  const [otherDiagnosing, setOtherDiagnosing] = useState(false);
  const [otherDiagnostic, setOtherDiagnostic] = useState(null);
  const [otherSandboxing, setOtherSandboxing] = useState(false);

  const isAdmin = user?.role === 'ADMIN';

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await getDemoStatus();
      setStatus(res.data);
      setStatusError(null);
    } catch (err) {
      // Operator-safe graceful degradation. The endpoint is operator-allowed
      // but if it ever fails (network, role change, backend restart), we show
      // a polished hint instead of an infinite spinner.
      setStatusError(err?.response?.data?.detail || 'Showcase dataset details are unavailable right now. Please ask your admin to refresh the showcase workspace.');
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    // Both admin and operator need showcase status — admin to drive the
    // Active/Inactive workspace card, operator to render the sandbox counts.
    loadStatus();
  }, []);

  const handleReset = async () => {
    if (!window.confirm('This rebuilds the sandboxed showcase dataset.\n\nYour real properties, guests, tasks, and history are NOT touched. Only existing showcase records will be replaced.\n\nContinue?')) {
      return;
    }
    setResetting(true);
    try {
      const res = await resetDemo();
      toast.success('Showcase workspace loaded.', { description: `${res.data.tasks_created} tasks across 5 lanes · 1 guest · 1 review` });
      await loadStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Couldn\u2019t reset showcase — ${detail || 'try again'}`);
    } finally {
      setResetting(false);
    }
  };

  const copyCreds = () => {
    navigator.clipboard?.writeText('demo@maris.local');
    toast.success('Showcase login email copied', { description: 'Use “Generate demo login link” to mint a password.' });
  };

  // ----------------------------------------------------------------
  // Demo login link — admin-only. Mints a one-time password-reset URL
  // for demo@maris.local and surfaces it in the modal so the admin can
  // open it / copy it. The URL never leaves the browser session.
  // ----------------------------------------------------------------
  const [demoLinkLoading, setDemoLinkLoading] = useState(false);
  const [demoLink, setDemoLink] = useState(null); // {resetUrl, expiresAt, expiresInSeconds, demoEmail}
  const [demoLinkOpen, setDemoLinkOpen] = useState(false);

  const handleMintDemoLink = async () => {
    setDemoLinkLoading(true);
    try {
      const res = await mintDemoResetLink();
      setDemoLink(res.data);
      setDemoLinkOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Couldn\u2019t mint demo login link — ${detail || 'try again'}`);
    } finally {
      setDemoLinkLoading(false);
    }
  };

  const copyDemoLink = async () => {
    try {
      await navigator.clipboard.writeText(demoLink?.resetUrl || '');
      toast.success('Demo login link copied');
    } catch {
      toast.error('Clipboard unavailable');
    }
  };

  const openDemoLink = () => {
    if (demoLink?.resetUrl) {
      window.open(demoLink.resetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSeedShowcase = async () => {
    if (!window.confirm('Populate the admin dashboard with realistic, fictional hospitality data?\n\nThis adds:\n• 2 urgent items, 4 due-today tasks, 3 follow-ups\n• 4 inbox conversations\n• Closed tickets sized so Financial Impact reads ≈ $2,145 this week\n\nAll records are tagged so re-running cleans them safely. No other data is touched.\n\nContinue?')) {
      return;
    }
    setSeedingShowcase(true);
    try {
      const res = await seedShowcaseData();
      setShowcaseSummary(res.data);
      toast.success('Showcase workspace loaded.', {
        description: `${res.data.urgent_open} urgent · ${res.data.due_today_open} due today · ${res.data.follow_ups_open} follow-ups · $${res.data.expected_financial_impact_total} FI`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Couldn\u2019t load showcase — ${detail || 'try again'}`);
    } finally {
      setSeedingShowcase(false);
    }
  };

  // Relocates any stray showcase records out of the LIVE workspace and into
  // the sandboxed demo property. Idempotent — safe to run repeatedly.
  const handlePurge = async () => {
    setPurging(true);
    try {
      const res = await purgeShowcaseFromLive();
      const { tasks = 0, conversations = 0, messages = 0 } = res.data || {};
      const total = tasks + conversations + messages;
      setLastPurge({ tasks, conversations, messages, total });
      if (total === 0) {
        toast.success('No stray showcase data found.', {
          description: 'Your LIVE workspace is already clean.',
        });
      } else {
        toast.success('Showcase records relocated.', {
          description: `${tasks} task${tasks === 1 ? '' : 's'} · ${conversations} conversation${conversations === 1 ? '' : 's'} · ${messages} message${messages === 1 ? '' : 's'} moved into the Showcase Workspace.`,
        });
      }
      await loadStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Couldn\u2019t purge stray showcase data — ${detail || 'try again'}`);
    } finally {
      setPurging(false);
      setPurgeOpen(false);
    }
  };

  // Read-only diagnostic — lists the exact records currently powering the
  // LIVE Today dashboard counts. Useful when the purge claims "clean" but
  // Today still shows non-zero numbers.
  const handleDiagnose = async () => {
    setDiagnosing(true);
    try {
      const res = await getLiveTodayDiagnostic();
      setDiagnostic(res.data);
      const counts = res.data?.counts || {};
      toast.success('Diagnostic loaded.', {
        description: `urgent_msgs=${counts.urgent_msgs} · due_soon_msgs=${counts.due_soon_msgs} · due_today_tasks=${counts.due_today_tasks} · handle_now_tasks=${counts.handle_now_tasks} · follow_up_tasks=${counts.follow_up_tasks}`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Couldn\u2019t fetch diagnostic — ${detail || 'try again'}`);
    } finally {
      setDiagnosing(false);
    }
  };

  // Targeted sandbox — flips every record currently surfaced by the
  // diagnostic to is_demo=True so they leave the LIVE workspace.
  const handleSandboxAll = async () => {
    if (!diagnostic) return;
    if (!window.confirm('Move every record listed in the diagnostic above into the Showcase Workspace?\n\nThis is reversible — admin can flip is_demo back on any record. It only touches the records currently shown in the diagnostic counts, nothing else.')) {
      return;
    }
    setSandboxing(true);
    try {
      const payload = {
        messageIds: [
          ...(diagnostic.urgent_messages || []),
          ...(diagnostic.due_soon_messages || []),
        ].map((m) => m.id).filter(Boolean),
        taskIds: [
          ...(diagnostic.due_today_tasks || []),
          ...(diagnostic.handle_now_tasks || []),
          ...(diagnostic.follow_up_tasks || []),
        ].map((t) => t.id).filter(Boolean),
      };
      const res = await sandboxRecordIds(payload);
      const { messages = 0, tasks = 0 } = res.data || {};
      toast.success('Records sandboxed.', {
        description: `${messages} message${messages === 1 ? '' : 's'} · ${tasks} task${tasks === 1 ? '' : 's'} moved out of LIVE. Hard-refresh Today to confirm.`,
      });
      // Refresh the diagnostic so the lists redraw empty
      const fresh = await getLiveTodayDiagnostic();
      setDiagnostic(fresh.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Couldn\u2019t sandbox records — ${detail || 'try again'}`);
    } finally {
      setSandboxing(false);
    }
  };

  // Diagnostic for Guest Inbox / Reviews / Guests / Follow-ups tabs.
  const handleDiagnoseOther = async () => {
    setOtherDiagnosing(true);
    try {
      const res = await getLiveOtherTabsDiagnostic();
      setOtherDiagnostic(res.data);
      const c = res.data?.counts || {};
      toast.success('Other-tabs diagnostic loaded.', {
        description: `inbox=${c.inbox_conversations} · reviews=${c.reviews} · guests=${c.guests} · followups=${c.followups}`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Couldn\u2019t fetch other-tabs diagnostic — ${detail || 'try again'}`);
    } finally {
      setOtherDiagnosing(false);
    }
  };

  // Sandbox every record listed by the other-tabs diagnostic.
  const handleSandboxAllOther = async () => {
    if (!otherDiagnostic) return;
    if (!window.confirm('Move every record listed below into the Showcase Workspace?\n\nThis covers Guest Inbox conversations, Reviews, Guests, and Follow-ups currently surfacing in LIVE. Reversible — sets is_demo=True on the listed records only.')) {
      return;
    }
    setOtherSandboxing(true);
    try {
      const payload = {
        conversationIds: (otherDiagnostic.inbox_conversations || []).map((r) => r.id).filter(Boolean),
        reviewIds:       (otherDiagnostic.reviews || []).map((r) => r.id).filter(Boolean),
        guestIds:        (otherDiagnostic.guests || []).map((r) => r.id).filter(Boolean),
        followupIds:     (otherDiagnostic.followups || []).map((r) => r.id).filter(Boolean),
      };
      const res = await sandboxRecordIds(payload);
      const { conversations = 0, reviews = 0, guests = 0, followups = 0 } = res.data || {};
      toast.success('Records sandboxed.', {
        description: `${conversations} convs · ${reviews} reviews · ${guests} guests · ${followups} followups moved out of LIVE. Hard-refresh the tabs to confirm.`,
      });
      const fresh = await getLiveOtherTabsDiagnostic();
      setOtherDiagnostic(fresh.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Couldn\u2019t sandbox records — ${detail || 'try again'}`);
    } finally {
      setOtherSandboxing(false);
    }
  };

  if (!isAdmin) {
    // Operator-facing Showcase landing — read-only practice workspace overview.
    // No admin tools, no copyable credentials, no diagnostic / sandbox controls.
    const sandbox = status?.sandbox || {};
    const totalRecords = Object.values(sandbox).reduce((a, b) => a + (b || 0), 0);
    return (
      <DeskLayout>
        <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="showcase-operator-page">
          <Alert className="border-emerald-200 bg-emerald-50/70 text-emerald-900" data-testid="operator-practice-banner">
            <Compass className="h-4 w-4 text-emerald-700" />
            <AlertTitle className="text-emerald-900">Practice workspace</AlertTitle>
            <AlertDescription className="text-emerald-800/90 text-[13.5px] leading-relaxed">
              Sandboxed records only. Actions here do not affect live property operations.
              Use this space to learn how Maris Desk works without touching real guests or tasks.
            </AlertDescription>
          </Alert>

          <div>
            <div className="flex items-center gap-2">
              <Compass className="h-5 w-5 text-[#3F617E]" />
              <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Showcase</h1>
            </div>
            <p className="text-stone-500 mt-1">
              A read-only overview of the sandboxed dataset prepared by your admin.
            </p>
          </div>

          <Card className="border-stone-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">Sandboxed dataset</CardTitle>
              <CardDescription>
                {loadingStatus
                  ? 'Loading…'
                  : statusError
                  ? statusError
                  : totalRecords === 0
                  ? 'The practice workspace has not been loaded yet. Ask your admin to load the showcase.'
                  : 'Counts visible only in the sandboxed scope. None of these records affect your LIVE workspace.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingStatus ? (
                <Loader2 className="h-5 w-5 animate-spin text-stone-400" />
              ) : statusError ? (
                <p className="text-[13px] text-stone-500">
                  Try refreshing the page. If the issue persists, please contact your admin.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="operator-sandbox-counts">
                  {[
                    ['Tasks', sandbox.tasks || 0],
                    ['Conversations', sandbox.conversations || 0],
                    ['Messages', sandbox.messages || 0],
                    ['Follow-ups', sandbox.followups || 0],
                    ['Guests', sandbox.guests || 0],
                    ['Reviews', sandbox.reviews || 0],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg border border-stone-200 bg-stone-50/60 px-4 py-3">
                      <p className="text-[10px] tracking-[0.18em] uppercase text-stone-500">{label}</p>
                      <p className="text-2xl font-heading text-stone-800 mt-1">{value}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-stone-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">How to practice</CardTitle>
              <CardDescription>
                To explore the sandboxed Today / Inbox / Daily Run with real-feeling data,
                ask your admin for the dedicated showcase login. That login is the only way
                to view sandboxed records in the actual dashboards — your own account is
                always scoped to LIVE data only.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-[13px] text-stone-600 leading-relaxed">
                Nothing you do under the showcase login affects real properties, guests,
                conversations, or tasks. It's a sandboxed copy maintained by your admin.
              </p>
            </CardContent>
          </Card>
        </div>
      </DeskLayout>
    );
  }

  return (
    <DeskLayout>
      <div className="space-y-6 animate-fade-in" data-testid="demo-page">
        {/* Admin-only banner — clarifies this is a controlled showcase workspace, separate from real operations */}
        <Alert
          className="border-amber-200 bg-amber-50/70 text-amber-900"
          data-testid="showcase-admin-banner"
        >
          <ShieldAlert className="h-4 w-4 text-amber-700" />
          <AlertTitle className="text-amber-900">Controlled showcase workspace</AlertTitle>
          <AlertDescription className="text-amber-800/90 text-[13.5px] leading-relaxed">
            This page is admin-only. The showcase workspace is a sandboxed dataset used for sales walkthroughs and internal verification. It is fully separated from your live property operations — loading or refreshing it never touches your real properties, guests, conversations, or tasks.
          </AlertDescription>
        </Alert>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Compass className="h-5 w-5 text-[#3F617E]" />
              <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Showcase Workspace</h1>
            </div>
            <p className="text-stone-500 mt-1">A 10-step walkthrough that takes a new viewer from login to a closed Concierge Briefing.</p>
            <p className="text-xs text-stone-400 mt-0.5">Sandboxed dataset — sits alongside real property operations without touching them.</p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {isAdmin && (
              <Button
                onClick={handleMintDemoLink}
                disabled={demoLinkLoading}
                className="bg-[#3F617E] hover:bg-[#345470] rounded-full w-full sm:w-auto order-1 sm:order-2"
                data-testid="mint-demo-link-btn"
              >
                {demoLinkLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4 mr-2" />
                )}
                {demoLinkLoading ? 'Generating…' : 'Generate demo login link'}
              </Button>
            )}
            <div className="flex items-center gap-2 sm:gap-3 order-2 sm:order-1">
              <Button variant="outline" className="rounded-full flex-1 sm:flex-none" onClick={copyCreds} data-testid="copy-demo-creds">
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy login
              </Button>
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={resetting}
                className="rounded-full flex-1 sm:flex-none"
                data-testid="reset-demo-btn"
              >
                {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                {resetting ? 'Loading…' : 'Rebuild showcase'}
              </Button>
            </div>
          </div>
        </div>

        {/* Status */}
        <Card className="border-stone-200">
          <CardContent className="p-4 flex items-center gap-3">
            {loadingStatus ? (
              <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
            ) : status.is_demo ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-stone-900">Showcase workspace is loaded</p>
                  <p className="text-xs text-stone-500">
                    Visible only when logged in as <span className="font-mono">demo@maris.local</span>.
                    Your real data ({status.real_properties || 0} {(status.real_properties || 0) === 1 ? 'property' : 'properties'}) is untouched.
                  </p>
                </div>
                <Badge className="bg-green-50 text-green-700 border-green-200">Active</Badge>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-amber-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-stone-900">No showcase dataset yet</p>
                  <p className="text-xs text-stone-500">
                    Click <span className="font-medium">Rebuild showcase</span> to build a sandboxed scenario.
                    Your real data ({status.real_properties || 0} {(status.real_properties || 0) === 1 ? 'property' : 'properties'}) stays put.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Showcase data — populates the live admin dashboard with realistic fictional data */}
        <Card className="border-stone-200">
          <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base font-heading">Showcase workspace (admin dashboard)</CardTitle>
              <CardDescription>
                Populates Today, Inbox, Daily Run, and Reports with realistic — but fully fictional — hospitality data.
                Designed for sales walkthroughs and internal verification. Re-runnable; never touches other real data.
              </CardDescription>
            </div>
            <Button
              onClick={handleSeedShowcase}
              disabled={seedingShowcase}
              variant="outline"
              className="rounded-full shrink-0"
              data-testid="seed-showcase-btn"
            >
              {seedingShowcase ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {seedingShowcase ? 'Loading…' : (showcaseSummary ? 'Refresh showcase' : 'Load showcase')}
            </Button>
          </CardHeader>
          {showcaseSummary && (
            <CardContent className="pt-0">
              <div className="text-xs text-stone-600 space-y-1 rounded-lg border border-stone-100 bg-stone-50/60 p-3">
                <p><span className="text-stone-400">Property:</span> <span className="font-medium text-stone-900">{showcaseSummary.property}</span></p>
                <p><span className="text-stone-400">Open work:</span> {showcaseSummary.urgent_open} urgent · {showcaseSummary.due_today_open} due today · {showcaseSummary.follow_ups_open} follow-ups</p>
                <p><span className="text-stone-400">Inbox:</span> {showcaseSummary.conversations_created} guest conversations ({showcaseSummary.intake_queue} pending intake)</p>
                <p><span className="text-stone-400">Closed this week:</span> {showcaseSummary.closed_for_financial_impact} tickets · Financial Impact ≈ ${showcaseSummary.expected_financial_impact_total}</p>
              </div>
              <div className="flex gap-2 mt-3">
                <Button asChild size="sm" className="bg-[#3F617E] hover:bg-[#345470] rounded-full">
                  <a href="/desk/today" data-testid="showcase-go-today">View Today →</a>
                </Button>
                <Button asChild size="sm" variant="outline" className="rounded-full">
                  <a href="/desk/inbox">Open Inbox</a>
                </Button>
                <Button asChild size="sm" variant="outline" className="rounded-full">
                  <a href="/desk/daily-run">Daily Run</a>
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Stray data cleanup — relocates any leaked showcase rows out of LIVE */}
        <Card className="border-stone-200">
          <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-stone-500" />
                Purge stray showcase data
              </CardTitle>
              <CardDescription>
                One-shot housekeeping: relocate any showcase records that are
                currently leaking into the LIVE Today / Guest Inbox / Daily Run
                back into the sandboxed Showcase Workspace. Safe to run anytime —
                only touches records tagged as showcase.
              </CardDescription>
            </div>
            <Button
              onClick={() => setPurgeOpen(true)}
              disabled={purging}
              variant="outline"
              className="rounded-full shrink-0"
              data-testid="purge-showcase-btn"
            >
              {purging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
              {purging ? 'Purging…' : 'Purge stray data'}
            </Button>
          </CardHeader>
          {lastPurge && (
            <CardContent className="pt-0">
              {lastPurge.total === 0 ? (
                <Alert className="border-emerald-200 bg-emerald-50/70" data-testid="purge-result-empty">
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                  <AlertTitle className="text-emerald-900">No stray showcase data found</AlertTitle>
                  <AlertDescription className="text-emerald-800/90 text-[13px]">
                    Your LIVE workspace is already clean. Re-running the purge does nothing.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="border-emerald-200 bg-emerald-50/70" data-testid="purge-result-success">
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                  <AlertTitle className="text-emerald-900">
                    Relocated {lastPurge.total} stray showcase record{lastPurge.total === 1 ? '' : 's'}
                  </AlertTitle>
                  <AlertDescription className="text-emerald-800/90 text-[13px]">
                    {lastPurge.tasks} task{lastPurge.tasks === 1 ? '' : 's'} ·{' '}
                    {lastPurge.conversations} conversation{lastPurge.conversations === 1 ? '' : 's'} ·{' '}
                    {lastPurge.messages} message{lastPurge.messages === 1 ? '' : 's'} moved into the Showcase Workspace.
                    Hard-refresh <span className="font-medium">Today</span>,{' '}
                    <span className="font-medium">Guest Inbox</span>, and{' '}
                    <span className="font-medium">Daily Run</span> to see the LIVE workspace clean.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          )}
        </Card>

        {/* DIAGNOSTIC — lists the exact records powering LIVE Today counts */}
        <Card className="border-stone-200" data-testid="diagnostic-card">
          <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-stone-500" />
                Diagnostic — what is LIVE Today actually counting?
              </CardTitle>
              <CardDescription>
                Read-only inspection of the exact records being surfaced by the
                LIVE Today dashboard. Use when the purge says "clean" but Today
                still shows non-zero counts. Lists message and task IDs with
                property, guest, and tag fields so we can identify them.
              </CardDescription>
            </div>
            <Button
              onClick={handleDiagnose}
              disabled={diagnosing}
              variant="outline"
              className="rounded-full shrink-0"
              data-testid="diagnostic-run-btn"
            >
              {diagnosing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldAlert className="h-4 w-4 mr-2" />}
              {diagnosing ? 'Loading…' : 'Run diagnostic'}
            </Button>
          </CardHeader>

          {diagnostic && (
            <CardContent className="pt-0 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
                {Object.entries(diagnostic.counts || {}).map(([k, v]) => (
                  <div
                    key={k}
                    className={`rounded-lg border px-3 py-2 ${v > 0 ? 'border-amber-200 bg-amber-50/70' : 'border-stone-200 bg-stone-50'}`}
                    data-testid={`diag-count-${k}`}
                  >
                    <p className="text-[10px] tracking-[0.15em] uppercase text-stone-500">{k.replace(/_/g, ' ')}</p>
                    <p className={`text-2xl font-heading ${v > 0 ? 'text-amber-900' : 'text-stone-700'}`}>{v}</p>
                  </div>
                ))}
              </div>

              {(diagnostic.counts && Object.values(diagnostic.counts).some((n) => n > 0)) && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3">
                  <p className="text-[13px] text-amber-900 leading-relaxed">
                    Sandbox all listed records (set <span className="font-mono text-[12px]">is_demo=True</span>) to move them out of LIVE. Reversible.
                  </p>
                  <Button
                    onClick={handleSandboxAll}
                    disabled={sandboxing}
                    className="bg-[#3F617E] hover:bg-[#345470] rounded-full shrink-0"
                    data-testid="diagnostic-sandbox-all-btn"
                  >
                    {sandboxing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {sandboxing ? 'Sandboxing…' : 'Sandbox all listed records'}
                  </Button>
                </div>
              )}

              {[
                ['urgent_messages', 'Urgent messages (priority=URGENT, needsReply=true)'],
                ['due_soon_messages', 'Due-soon messages (dueBy within 2h, needsReply=true)'],
                ['due_today_tasks', 'Due-today tasks (priority=DUE_TODAY, open)'],
                ['handle_now_tasks', 'Handle-now tasks (priority=HANDLE_NOW, open)'],
                ['follow_up_tasks', 'Follow-up tasks (next_touch_at set, open)'],
              ].map(([key, label]) => {
                const rows = diagnostic[key] || [];
                if (rows.length === 0) return null;
                return (
                  <div key={key} className="space-y-2">
                    <p className="text-[11px] tracking-[0.18em] uppercase text-stone-500 font-medium">
                      {label} — {rows.length}
                    </p>
                    <div className="rounded-lg border border-stone-200 overflow-hidden">
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-[12px]">
                          <thead className="bg-stone-50 text-stone-600 text-left sticky top-0">
                            <tr>
                              <th className="px-3 py-2 font-medium">id</th>
                              <th className="px-3 py-2 font-medium">propertyName · slug</th>
                              <th className="px-3 py-2 font-medium">guest</th>
                              <th className="px-3 py-2 font-medium">priority/status</th>
                              <th className="px-3 py-2 font-medium">is_demo / marker</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-100">
                            {rows.map((r) => (
                              <tr key={r.id} className="hover:bg-stone-50/60">
                                <td className="px-3 py-2 font-mono text-[10.5px] text-stone-500">{r.id?.slice(0, 8)}…</td>
                                <td className="px-3 py-2 text-stone-700">
                                  {r.propertyName || <em className="text-stone-400">unknown</em>}
                                  <span className="text-stone-400 ml-1">· {r.propertySlug || '—'}</span>
                                </td>
                                <td className="px-3 py-2 text-stone-700">{r.guestName || <em className="text-stone-400">—</em>}</td>
                                <td className="px-3 py-2 text-stone-700">
                                  {r.priority || '—'}
                                  {r.status ? <span className="text-stone-400 ml-1">/ {r.status}</span> : null}
                                </td>
                                <td className="px-3 py-2 text-stone-500 font-mono text-[10.5px]">
                                  {String(r.is_demo)} {r._seed_marker ? `· ${r._seed_marker}` : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}

              <details className="text-[12.5px] text-stone-600">
                <summary className="cursor-pointer text-stone-700 font-medium">All properties (debug)</summary>
                <div className="mt-2 rounded-lg border border-stone-200 overflow-hidden">
                  <table className="w-full text-[12px]">
                    <thead className="bg-stone-50 text-stone-600 text-left">
                      <tr>
                        <th className="px-3 py-2 font-medium">name</th>
                        <th className="px-3 py-2 font-medium">slug</th>
                        <th className="px-3 py-2 font-medium">is_demo</th>
                        <th className="px-3 py-2 font-medium">marker</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {(diagnostic.all_properties || []).map((p) => (
                        <tr key={p.id}>
                          <td className="px-3 py-2 text-stone-700">{p.name}</td>
                          <td className="px-3 py-2 text-stone-500 font-mono text-[11px]">{p.slug}</td>
                          <td className="px-3 py-2 text-stone-500 font-mono text-[11px]">{String(p.is_demo)}</td>
                          <td className="px-3 py-2 text-stone-500 font-mono text-[11px]">{p._seed_marker || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </CardContent>
          )}
        </Card>

        {/* DIAGNOSTIC #2 — Guest Inbox / Reviews / Guests / Follow-ups */}
        <Card className="border-stone-200" data-testid="diagnostic-other-card">
          <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-stone-500" />
                Diagnostic — Guest Inbox, Reviews, Guests, Follow-ups
              </CardTitle>
              <CardDescription>
                Read-only inspection of the other LIVE tabs. Same is_demo!=True
                scope used by the public endpoints. Use to find leftover seed
                or demo records that are still surfacing outside Today.
              </CardDescription>
            </div>
            <Button
              onClick={handleDiagnoseOther}
              disabled={otherDiagnosing}
              variant="outline"
              className="rounded-full shrink-0"
              data-testid="diagnostic-other-run-btn"
            >
              {otherDiagnosing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldAlert className="h-4 w-4 mr-2" />}
              {otherDiagnosing ? 'Loading…' : 'Run diagnostic'}
            </Button>
          </CardHeader>

          {otherDiagnostic && (
            <CardContent className="pt-0 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                {Object.entries(otherDiagnostic.counts || {}).map(([k, v]) => (
                  <div
                    key={k}
                    className={`rounded-lg border px-3 py-2 ${v > 0 ? 'border-amber-200 bg-amber-50/70' : 'border-stone-200 bg-stone-50'}`}
                    data-testid={`diag-other-count-${k}`}
                  >
                    <p className="text-[10px] tracking-[0.15em] uppercase text-stone-500">{k.replace(/_/g, ' ')}</p>
                    <p className={`text-2xl font-heading ${v > 0 ? 'text-amber-900' : 'text-stone-700'}`}>{v}</p>
                  </div>
                ))}
              </div>

              {(otherDiagnostic.counts && Object.values(otherDiagnostic.counts).some((n) => n > 0)) && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3">
                  <p className="text-[13px] text-amber-900 leading-relaxed">
                    Sandbox every listed conversation, review, guest, and follow-up
                    (set <span className="font-mono text-[12px]">is_demo=True</span>) so they leave LIVE. Reversible.
                  </p>
                  <Button
                    onClick={handleSandboxAllOther}
                    disabled={otherSandboxing}
                    className="bg-[#3F617E] hover:bg-[#345470] rounded-full shrink-0"
                    data-testid="diagnostic-other-sandbox-all-btn"
                  >
                    {otherSandboxing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {otherSandboxing ? 'Sandboxing…' : 'Sandbox all listed records'}
                  </Button>
                </div>
              )}

              {[
                ['inbox_conversations', 'Guest Inbox conversations'],
                ['reviews', 'Reviews'],
                ['guests', 'Guests Directory'],
                ['followups', 'Follow-ups'],
              ].map(([key, label]) => {
                const rows = otherDiagnostic[key] || [];
                if (rows.length === 0) return null;
                return (
                  <div key={key} className="space-y-2">
                    <p className="text-[11px] tracking-[0.18em] uppercase text-stone-500 font-medium">
                      {label} — {rows.length}
                    </p>
                    <div className="rounded-lg border border-stone-200 overflow-hidden">
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-[12px]">
                          <thead className="bg-stone-50 text-stone-600 text-left sticky top-0">
                            <tr>
                              <th className="px-3 py-2 font-medium">id</th>
                              <th className="px-3 py-2 font-medium">propertyName · slug</th>
                              <th className="px-3 py-2 font-medium">guest</th>
                              <th className="px-3 py-2 font-medium">detail</th>
                              <th className="px-3 py-2 font-medium">is_demo / marker</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-100">
                            {rows.map((r) => (
                              <tr key={r.id} className="hover:bg-stone-50/60">
                                <td className="px-3 py-2 font-mono text-[10.5px] text-stone-500">{r.id?.slice(0, 8)}…</td>
                                <td className="px-3 py-2 text-stone-700">
                                  {r.propertyName || <em className="text-stone-400">unknown</em>}
                                  <span className="text-stone-400 ml-1">· {r.propertySlug || '—'}</span>
                                </td>
                                <td className="px-3 py-2 text-stone-700">{r.guestName || <em className="text-stone-400">—</em>}</td>
                                <td className="px-3 py-2 text-stone-700">
                                  {key === 'reviews' && `${r.platform || '—'} · ${r.rating || '—'}★`}
                                  {key === 'followups' && `${r.type || '—'} · ${r.status || '—'}`}
                                  {key === 'guests' && (r.email || r.phone || '—')}
                                  {key === 'inbox_conversations' && (r.subject || r.channel || '—')}
                                </td>
                                <td className="px-3 py-2 text-stone-500 font-mono text-[10.5px]">
                                  {String(r.is_demo)} {r._seed_marker ? `· ${r._seed_marker}` : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          )}
        </Card>

        {/* Credentials card */}
        <Card className="border-stone-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Showcase accounts</CardTitle>
            <CardDescription>
              Passwords are never stored in this app. Issue a one-time reset link to set the password, then share the credentials manually.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="flex items-center justify-between bg-stone-50 rounded-md px-3 py-2 border border-stone-100">
              <div>
                <p className="text-xs text-stone-400">Showcase (sandboxed)</p>
                <p className="text-sm font-mono">demo@maris.local</p>
              </div>
              <Badge variant="outline" className="text-[10px]">DEMO role</Badge>
            </div>
            <div className="flex items-center justify-between bg-stone-50 rounded-md px-3 py-2 border border-stone-100">
              <div>
                <p className="text-xs text-stone-400">Admin (real data)</p>
                <p className="text-sm font-mono">admin@maris.local</p>
              </div>
              <Badge variant="outline" className="text-[10px]">ADMIN role</Badge>
            </div>
            <p className="text-[11px] text-stone-500 mt-2 leading-relaxed">
              To set a password as an admin: click <span className="font-medium text-stone-700">&ldquo;Generate demo login link&rdquo;</span> above, then open the one-time URL and set a fresh password.
            </p>
          </CardContent>
        </Card>

        {/* Walkthrough steps */}
        <div>
          <h2 className="text-lg font-heading text-stone-900 mb-3">Walkthrough</h2>
          <div className="space-y-3">
            {WALKTHROUGH.map((s) => (
              <Step key={s.n} step={s} onOpen={(p) => navigate(p)} />
            ))}
          </div>
        </div>

        <Alert className="bg-stone-50 border-stone-200">
          <AlertTitle className="text-sm">After step 10</AlertTitle>
          <AlertDescription className="text-xs">
            Click the eye icon "Past briefings" on Daily Run to view the now-locked Concierge Briefing. Then come back here and click "Rebuild showcase" to start over.
          </AlertDescription>
        </Alert>
      </div>

      {/* Demo login link dialog (admin-only) — shows the one-time URL */}
      <AlertDialog open={demoLinkOpen} onOpenChange={setDemoLinkOpen}>
        <AlertDialogContent data-testid="demo-link-dialog" className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>One-time demo login link</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-stone-600">
                <p>
                  Open this URL in a new tab to set a password for{' '}
                  <span className="font-mono text-stone-900">{demoLink?.demoEmail || 'demo@maris.local'}</span>.
                  Then sign in at <span className="font-mono text-stone-900">/desk/login</span> with that password.
                </p>
                <div className="bg-stone-50 border border-stone-200 rounded-md p-3 break-all font-mono text-[11.5px] text-stone-800 select-all" data-testid="demo-link-url">
                  {demoLink?.resetUrl || ''}
                </div>
                <p className="text-[12px] text-stone-500">
                  Valid for <span className="font-medium">1 hour</span>, single-use. Any previously-issued link for this account is now invalidated.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <Button variant="outline" onClick={copyDemoLink} className="rounded-full" data-testid="demo-link-copy-btn">
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
            </Button>
            <Button onClick={openDemoLink} className="rounded-full bg-[#3F617E] hover:bg-[#345470]" data-testid="demo-link-open-btn">
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open in new tab
            </Button>
            <AlertDialogCancel data-testid="demo-link-close">Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation dialog for the purge action */}
      <AlertDialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <AlertDialogContent data-testid="purge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Relocate stray showcase data?</AlertDialogTitle>
            <AlertDialogDescription>
              This only touches records tagged with the Showcase marker. It
              moves any stray showcase rows out of the LIVE workspace and back
              into the sandboxed Showcase Workspace.
              <br /><br />
              Your real properties, guests, conversations, and tasks are{' '}
              <span className="font-medium text-stone-900">not touched</span>.
              The action is safe to run multiple times — re-running on a clean
              workspace does nothing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purging} data-testid="purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePurge}
              disabled={purging}
              className="bg-[#3F617E] hover:bg-[#345470]"
              data-testid="purge-confirm"
            >
              {purging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
              {purging ? 'Relocating…' : 'Yes, relocate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DeskLayout>
  );
};

export default Demo;
